import { useMemo, useState } from 'react'
import {
  Box, MenuItem, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography,
} from '@mui/material'
import { CircleAlert, Clock, Flag as FlagIcon } from 'lucide-react'
import {
  AppShell, Crumb, EmptyState, Flag as CountryFlag, Segmented, SkeletonCards, Surface, formatDate, tokens, useNavigate,
} from '@agribot/polygon-checks-ui'
import { useQueue } from '../hooks'
import { LEVEL_LABEL, LEVEL_ROLE } from '../rules'
import type { QueueRow, ReviewLevel } from '../types'
import { StatusPill } from '../components/StatusPill'

const ALL = ''

/**
 * Screen 8: submissions waiting for a review level. Overdue ones (waiting
 * longer than the API's threshold at their level) come first and say so in
 * words, not only in colour.
 */
export function ReviewQueue() {
  const navigate = useNavigate()
  const { data, isPending, error } = useQueue()
  const rows = data?.items
  const [level, setLevel] = useState<'1' | '2' | 'done'>('1')
  const [commodity, setCommodity] = useState(ALL)
  const [region, setRegion] = useState(ALL)
  const [parent, setParent] = useState(ALL)

  const counts = useMemo(() => ({
    1: rows?.filter((r) => r.level === 1).length ?? 0,
    2: rows?.filter((r) => r.level === 2).length ?? 0,
    done: rows?.filter((r) => r.level === null).length ?? 0,
  }), [rows])

  const atLevel = (rows ?? []).filter((r) => (level === 'done' ? r.level === null : r.level === Number(level) as ReviewLevel))
  const options = (pick: (r: QueueRow) => string[]) => [...new Set((rows ?? []).flatMap(pick))].sort()
  const shown = atLevel
    .filter((r) => !commodity || r.commodities.includes(commodity))
    .filter((r) => !region || r.region === region)
    .filter((r) => !parent || r.parent.name === parent)
    .sort((a, b) => Number(b.overdue) - Number(a.overdue) || b.daysInQueue - a.daysInQueue)

  return (
    <AppShell crumbs={[<Crumb key="a">Sourcing</Crumb>, <Crumb key="b">Reviews</Crumb>]}>
      <Box sx={{ flex: 1, overflow: 'auto', px: 8, py: 7 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 6, mb: 6, flexWrap: 'wrap' }}>
          <Box sx={{ flex: 1, minWidth: 280 }}>
            <Typography variant="h1" sx={{ mb: 2 }}>Reviews</Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', maxWidth: 640 }}>
              Supplier submissions in the 2026 campaign. The material’s buyer reviews first; the region’s
              sustainable-sourcing lead gives the final approval. Either can return a submission with comments.
            </Typography>
          </Box>
          <Segmented
            ariaLabel="Review level"
            value={level}
            onChange={(v) => setLevel(v as typeof level)}
            options={[
              { value: '1', label: `${LEVEL_LABEL[1]} · ${counts[1]}` },
              { value: '2', label: `${LEVEL_LABEL[2]} · ${counts[2]}` },
              { value: 'done', label: `Approved or returned · ${counts.done}` },
            ]}
          />
        </Box>

        <Box sx={{ display: 'flex', gap: 3, mb: 4, flexWrap: 'wrap' }}>
          <Filter label="Commodity" value={commodity} onChange={setCommodity} options={options((r) => r.commodities)} />
          <Filter label="Region" value={region} onChange={setRegion} options={options((r) => [r.region])} />
          <Filter label="Parent company" value={parent} onChange={setParent} options={options((r) => [r.parent.name])} />
        </Box>

        {isPending && <SkeletonCards columns="repeat(3, 1fr)" />}
        {error && <Typography color="error">{(error as Error).message}</Typography>}
        {rows && (
          <Surface sx={{ overflowX: 'auto' }}>
            {shown.length === 0 ? (
              <EmptyState
                title={level === 'done' ? 'Nothing decided yet' : 'Nothing waiting'}
                body={level === 'done'
                  ? 'Submissions you approve or return appear here.'
                  : `Submissions waiting for ${LEVEL_LABEL[Number(level) as ReviewLevel].toLowerCase()} by a ${LEVEL_ROLE[Number(level) as ReviewLevel].toLowerCase()} appear here.`}
              />
            ) : (
              <Table sx={{ minWidth: 900 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Supplier</TableCell>
                    <TableCell>Materials</TableCell>
                    <TableCell>Submitted</TableCell>
                    <TableCell>In queue</TableCell>
                    <TableCell>Checks</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {shown.map((r) => (
                    <TableRow
                      key={r.id}
                      hover
                      tabIndex={0}
                      onClick={() => navigate(`/sourcing/reviews/${r.id}`)}
                      onKeyDown={(e) => { if (e.key === 'Enter') navigate(`/sourcing/reviews/${r.id}`) }}
                      sx={{ cursor: 'pointer', '& td': { height: 64 } }}
                    >
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <CountryFlag code={r.supplier.countryCode} />
                          <Box>
                            <Box sx={{ fontWeight: 500, color: 'text.primary' }}>{r.supplier.name}</Box>
                            <Box sx={{ color: 'text.secondary', fontSize: 12 }}>
                              {r.parent.name !== r.supplier.name ? `${r.parent.name} · ` : ''}{r.region}
                            </Box>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box>{r.materials}</Box>
                        <Box sx={{ color: 'text.secondary', fontSize: 12 }}>{r.commodities.join(', ')}</Box>
                      </TableCell>
                      <TableCell>{formatDate(r.submittedAt)}</TableCell>
                      <TableCell><QueueAge row={r} /></TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                          {r.failedChecks > 0
                            ? <Inline icon={<CircleAlert size={14} strokeWidth={2} />} colour={tokens.color.danger}>{r.failedChecks} failed</Inline>
                            : <Box component="span" sx={{ color: 'text.secondary' }}>All passed</Box>}
                          {r.openFlags > 0 && <Inline icon={<FlagIcon size={13} strokeWidth={2} />} colour={tokens.color.warning}>{r.openFlags} flagged</Inline>}
                        </Box>
                      </TableCell>
                      <TableCell><StatusPill status={r.status} /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Surface>
        )}
        {data && (
          <Typography variant="caption" component="p" sx={{ color: 'text.secondary', mt: 3 }}>
            Overdue: waiting more than {data.overdueAfterDays} days at one level.
          </Typography>
        )}
      </Box>
    </AppShell>
  )
}

function QueueAge({ row }: { row: QueueRow }) {
  if (row.level === null) return <Box component="span" sx={{ color: 'text.secondary' }}>—</Box>
  const late = row.overdue
  return (
    <Inline icon={late ? <Clock size={14} strokeWidth={2} /> : null} colour={late ? tokens.color.danger : undefined} bold={late}>
      {row.daysInQueue} day{row.daysInQueue === 1 ? '' : 's'}{late ? ' · overdue' : ''}
    </Inline>
  )
}

function Inline({ icon, colour, bold, children }: { icon: React.ReactNode; colour?: string; bold?: boolean; children: React.ReactNode }) {
  return (
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, color: colour ?? 'text.primary', fontWeight: bold ? 500 : 400 }}>
      {icon}{children}
    </Box>
  )
}

function Filter({ label, value, onChange, options }: { label: string; value: string; onChange: (v: string) => void; options: string[] }) {
  return (
    <TextField
      select
      label={label}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      sx={{ minWidth: 200 }}
      InputLabelProps={{ shrink: true }}
      SelectProps={{ displayEmpty: true }}
    >
      <MenuItem value={ALL}>All</MenuItem>
      {options.map((o) => <MenuItem key={o} value={o}>{o}</MenuItem>)}
    </TextField>
  )
}
